/**
 * GroupJurisdictionTab: self-contained jurisdiction view for the group scope.
 *
 * Fetches bank accounts and wealth assets, computes per-jurisdiction
 * aggregates, and renders the shared ByJurisdictionView.
 *
 * Uses function Foo(props) with body destructuring per 147f605 guard.
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useEntityStore } from '../../../store';
import { bankAccountsAPI, wealthAPI } from '../../../lib/api';
import { JURISDICTION_CONFIG } from '../../../lib/jurisdictionConfig';
import { bucketAccountsByJurisdiction } from '../../../lib/jurisdictionBuckets';
import { makeLiveBalanceConverter, formatCurrencyShort } from '../../../lib/displayCurrency';
import { applyScopeChange } from '../../../lib/scopeNavigation';
import { ByJurisdictionView } from '../ByJurisdictionView';

export function GroupJurisdictionTab() {
  var navigate = useNavigate();
  var entities = useEntityStore(function (s) { return s.entities; });
  var setSelectedEntity = useEntityStore(function (s) { return s.setSelectedEntity; });
  var setSelectedScope = useEntityStore(function (s) { return s.setSelectedScope; });

  var accountsState = useState([]);
  var bankAccountsVal = accountsState[0];
  var setBankAccountsVal = accountsState[1];

  var assetsState = useState([]);
  var wealthAssets = assetsState[0];
  var setWealthAssets = assetsState[1];

  // Live full-precision spot rates from /bank-balances/summary (get_rate_safe,
  // the SAME source the bank_balance_positions predicate converts through).
  // Replaces the hardcoded FX_TO_EUR approximation so the jurisdiction card
  // totals match the predicate (and the Overall Cash Now) to the penny.
  var fxState = useState({});
  var fxRatesSpot = fxState[0];
  var setFxRatesSpot = fxState[1];

  var loadingState = useState(true);
  var isLoading = loadingState[0];
  var setIsLoading = loadingState[1];

  useEffect(function () {
    var cancelled = false;
    var fetchData = async function () {
      try {
        var results = await Promise.allSettled([
          // Canonical bank_balance_positions predicate (via /bank-balances/summary):
          // deduped, one real account once, entity resolved through the registry.
          // Group scope (no entity_id/scope) so every account is present and the
          // jurisdiction buckets reconcile to the group total.
          bankAccountsAPI.getSummary({}),
          wealthAPI.getAssets(),
        ]);
        if (cancelled) return;
        if (results[0].status === 'fulfilled') {
          var payload = results[0].value.data || {};
          var rows = payload.accounts || [];
          // balances arrive as lossless strings; parse to number for bucketing.
          // A null balance ("feed never reported") stays null here -- it must
          // NOT become 0, or bucketAccountsByJurisdiction would silently sum
          // it into a real total instead of excluding it.
          setBankAccountsVal(rows.map(function (a) {
            return Object.assign({}, a, { balance: a.balance == null ? null : Number(a.balance) });
          }));
          setFxRatesSpot(payload.fx_rates_spot || {});
        }
        if (results[1].status === 'fulfilled') setWealthAssets(results[1].value.data || []);
      } catch (err) {
        console.error('GroupJurisdictionTab fetch error:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return function () { cancelled = true; };
  }, []);

  var entityById = useMemo(function () {
    return Object.fromEntries(entities.map(function (e) { return [e.id, e]; }));
  }, [entities]);

  var entityByType = useMemo(function () {
    var map = {};
    for (var i = 0; i < entities.length; i++) {
      if (!map[entities[i].type]) map[entities[i].type] = entities[i];
    }
    return map;
  }, [entities]);

  var grouped = useMemo(function () {
    // Bucketing is predicate-driven (each account carries a server `jurisdiction`
    // field); the store maps only feed per-entity display names.
    return bucketAccountsByJurisdiction(bankAccountsVal, entityById, entityByType);
  }, [bankAccountsVal, entityById, entityByType]);

  var convertBalances = useMemo(function () {
    return makeLiveBalanceConverter(fxRatesSpot);
  }, [fxRatesSpot]);

  var handleEntitySelect = function (entityId) {
    // Drill into a single entity: switch to the entities scope and navigate
    // to its dashboard (canonical handler, same as the scope picker). This is
    // required because this tab can be hosted on the Overall route, which
    // does not re-render on scope change.
    applyScopeChange('entities', { setSelectedScope, setSelectedEntity, navigate, entity: entityId });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[30vh]">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <ByJurisdictionView
      countryConfig={JURISDICTION_CONFIG}
      balByJurisdiction={grouped.balByJurisdiction}
      balByEntity={grouped.balByEntity}
      unassigned={grouped.unassigned}
      wealthAssets={wealthAssets}
      entities={entities}
      onEntitySelect={handleEntitySelect}
      convertBalances={convertBalances}
      formatCurrency={formatCurrencyShort}
    />
  );
}
